import React, { useEffect, useRef, useState } from 'react'

import { Badge } from './Badge'
import { useStartPlaying } from './StartPlaying'
import { useDisclosureModal, DisclosurePopover } from './Disclosure'

import { supportsTouchEvents } from '@/util/browserHelpers'
import { classNames } from '@/util/tailwindHelpers'
import useBreakpoints from '@/hooks/useBreakpoints'
import { useOnClickOutside } from '@/hooks/useOnClickOutside'

import { useArcade } from '@/features/arcade/provider'
import { useArcadeRouting } from '@/features/arcade/hooks/routing'

const MOUSE_OVER_ENABLED_DELAY = 50 as const

export const BadgeNav = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [mouseOverDisabled, setMouseOverDisabled] = useState<boolean>(true)
  const { leaderboard } = useArcadeRouting()

  const triggerRef = useRef()
  useOnClickOutside([triggerRef], () => isOpen && setIsOpen(false))

  const { isMobile } = useBreakpoints()
  const isTouchDevice = supportsTouchEvents()

  useDisclosureModal({ isOpen })

  const { isArcadeLoaded, player } = useArcade()

  const { startPlaying } = useStartPlaying()

  const isNotPlaying = player?.isNotPlaying
  const ignoreMouseOvers =
    isNotPlaying || isTouchDevice || isMobile || mouseOverDisabled

  const handleMouseEnter = () => {
    if (ignoreMouseOvers) return
    setIsOpen(true)
  }

  const handleMouseLeave = () => {
    if (ignoreMouseOvers) return
    setIsOpen(false)
  }

  const handleBadgeClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation()

    if (isNotPlaying) {
      startPlaying()
      return
    }

    if (!isMobile) {
      leaderboard.goTo().then()
      return
    }

    if (!isOpen || isTouchDevice) {
      setIsOpen(!isOpen)
    }
  }

  useEffect(() => {
    if (isOpen && (isNotPlaying || !isArcadeLoaded)) {
      setIsOpen(false)
    }
  }, [isOpen, isNotPlaying, isArcadeLoaded])

  useEffect(() => {
    const timer = setTimeout(() => {
      setMouseOverDisabled(false)
    }, MOUSE_OVER_ENABLED_DELAY)

    return () => clearTimeout(timer)
  }, [])

  const isPopoverOpen = !isMobile && isOpen

  return (
    <div
      className={classNames('relative cursor-pointer z-10', {
        'pt-2': isPopoverOpen
      })}
      ref={triggerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Badge onClick={handleBadgeClick} />
      <DisclosurePopover isOpen={isPopoverOpen} />
    </div>
  )
}
